import React, { useState, useEffect, useCallback, useRef } from "react";
import "./LoginPage.css";
import API_BASE_URL from "./RestAPI";
import { showSuccessToast, showErrorToast } from "../constants/ToastService";
import logo from "../constants/TsLogo.png";
import { useNavigate } from "react-router-dom";
import generateEmployeeAvatars from "./EmployeeHash";

const CAROUSEL_SLIDES = [
    {
        tagline: "Plan Sprints,\nShip Faster",
        subtitle:
            "Organize your backlog, plan sprints, and deliver features on schedule with powerful agile tools.",
    },
    {
        tagline: "Track Issues,\nDeliver Results",
        subtitle:
            "From bug reports to feature requests — capture, prioritize, and resolve every ticket effortlessly.",
    },
    {
        tagline: "Collaborate Seamlessly,\nBuild Together",
        subtitle:
            "Keep your team aligned with real-time Kanban boards, comments, and status updates.",
    },
    {
        tagline: "From Backlog\nto Done",
        subtitle:
            "Gain full visibility into your project lifecycle with smart dashboards and sprint analytics.",
    },
];

const CAROUSEL_INTERVAL = 5000;

const LoginPage = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ username: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [activeSlide, setActiveSlide] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const submittingRef = useRef(false);

    const routePath = {
        Dashboards: "/dashboard",
        Masters: "/MastersTab",
        Sprints: "/Sprints",
        "Kanban Chart": "/KanbanBoard",
        "Client Requirements": "/Client Requirements",
        Reports: "/reports",
        "Company Details": "/Company",
        "Customer Support": "/customer-support",
        "Client Onboarding": "/Onboarding",
    };

    // Auto-rotate carousel
    const nextSlide = useCallback(() => {
        setActiveSlide((prev) => (prev + 1) % CAROUSEL_SLIDES.length);
    }, []);

    useEffect(() => {
        localStorage.clear();
    }, []);

    useEffect(() => {
        const timer = setInterval(nextSlide, CAROUSEL_INTERVAL);
        return () => clearInterval(timer);
    }, [nextSlide]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const getCompanyEmployees = () => {
        fetch(`${API_BASE_URL}core/employee/`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
        })
            .then((res) => res.json())
            .then((data) => {
                if (data.status === "success") {
                    generateEmployeeAvatars(data.data);
                } else {
                    showErrorToast("Failed to load employees");
                }
            })
            .catch((err) => console.error("Error fetching employees:", err));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        // A ref, not just state: a fast double click (or Enter + click) fires
        // twice before the re-render that disables the button.
        if (submittingRef.current) return;
        submittingRef.current = true;
        setSubmitting(true);

        try {
            const response = await fetch(`${API_BASE_URL}auth/login/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            const data = await response.json();

            if (data.status === "success") {
                showSuccessToast(data.message);
                if (data.token !== "") {
                    localStorage.setItem("authToken", data.token);
                    localStorage.setItem("loginData", JSON.stringify(data));
                    localStorage.setItem(
                        "loginPermissions",
                        JSON.stringify(data.data.user.permissions)
                    );
                    localStorage.setItem(
                        "encryptionKey",
                        "VQJudi2xmFG2NihETwXPW6VTnL7zdQGto10FrmwnIUc="
                    );

                    const default_permissions = {
                        Dashboards: data.data.user.permissions["Dashboards"]
                            ? true
                            : false,
                        Sprints: data.data.user.permissions["Sprints"]
                            ? true
                            : false,
                        "Delete Ticket": data.data.user.permissions[
                            "Delete Ticket"
                        ]
                            ? true
                            : false,
                        "Kanban Chart": data.data.user.permissions[
                            "Kanban Chart"
                        ]
                            ? true
                            : false,
                        "Client Requirements": data.data.user.permissions[
                            "Client Requirements"
                        ]
                            ? true
                            : false,
                        Reports: data.data.user.permissions["Reports"]
                            ? true
                            : false,
                        Masters: data.data.user.permissions["Masters"]
                            ? true
                            : false,
                        "Error Tracking": data.data.user.permissions["Error Tracking"]
                            ? true
                            : false,
                        "Customer Support": data.data.user.permissions["Customer Support"],
                        "Request Live Move": data.data.user.permissions[
                            "Request Live Move"
                        ]
                            ? true
                            : false,
                        "Approve Live Move": data.data.user.permissions[
                            "Approve Live Move"
                        ]
                            ? true
                            : false,
                        "Client Onboarding": data.data.user.permissions[
                            "Client Onboarding"
                        ]
                            ? true
                            : false,
                        "Task Assignment": data.data.user.permissions[
                            "Task Assignment"
                        ]
                            ? true
                            : false,
                        // Quality module. Names must match SidePanel's
                        // screenNames and quality/constants.py PERM_*.
                        ...Object.fromEntries(
                            [
                                "Quality",
                                "Log Production Bug",
                                "Manage Releases",
                                "Run Regression",
                                "Manage Test Cases",
                                "Approve RCA",
                            ].map((p) => [p, !!data.data.user.permissions[p]])
                        ),
                    };

                    const trueKeys = Object.entries(default_permissions)
                        .filter(([_, value]) => value === true)
                        .map(([key]) => key);
                    localStorage.setItem(
                        "userPermissions",
                        JSON.stringify(trueKeys)
                    );
                    const firstTrueKey = trueKeys[0];

                    // Check whether this company has the Live Support Sprint
                    // feature enabled (Django admin → Configuration). Cache it so
                    // the rest of the app can read it immediately after login.
                    try {
                        const featureRes = await fetch(
                            `${API_BASE_URL}core/feature-config/`,
                            {
                                method: "GET",
                                headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${data.token}`,
                                },
                            }
                        );
                        const featureData = await featureRes.json();
                        const hasLiveSupportSprint = Boolean(
                            featureData?.data?.live_support_sprint
                        );
                        const hasReleaseApproval = Boolean(
                            featureData?.data?.release_approval
                        );
                        console.log(
                            "Current company has Live Support Sprint:",
                            hasLiveSupportSprint,
                            "| Release Approval:",
                            hasReleaseApproval
                        );
                        localStorage.setItem(
                            "liveSupportEnabled",
                            JSON.stringify(hasLiveSupportSprint)
                        );
                        localStorage.setItem(
                            "releaseApprovalEnabled",
                            JSON.stringify(hasReleaseApproval)
                        );
                        localStorage.setItem(
                            "releaseEligibleStatuses",
                            JSON.stringify(
                                featureData?.data?.release_eligible_statuses || []
                            )
                        );
                        localStorage.setItem(
                            "qaTimeTrackingEnabled",
                            JSON.stringify(
                                Boolean(featureData?.data?.qa_time_tracking)
                            )
                        );
                    } catch (featErr) {
                        console.error("Failed to load feature config:", featErr);
                    }

                    getCompanyEmployees();
                    navigate(routePath[firstTrueKey]);
                } else {
                    showErrorToast("Invalid Login");
                }
            } else {
                showErrorToast(data.message);
            }
        } catch (err) {
            console.error("Failed to save data:", err);
            showErrorToast("Something went wrong. Please try again.");
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    };

    return (
        <div className="login-page">
            {/* Left — Carousel */}
            <div className="login-carousel">
                <div className="login-carousel__inner">
                    <img
                        src={logo}
                        alt="Tecnospice"
                        className="login-carousel__logo"
                    />

                    {CAROUSEL_SLIDES.map((slide, index) => (
                        <div
                            key={index}
                            className={`login-carousel__slide login-carousel__slide--${index + 1} ${
                                activeSlide === index
                                    ? "login-carousel__slide--active"
                                    : ""
                            }`}
                        >
                            <div className="login-carousel__text">
                                <h2 className="login-carousel__tagline">
                                    {slide.tagline}
                                </h2>
                                <p className="login-carousel__subtitle">
                                    {slide.subtitle}
                                </p>

                                <div className="login-carousel__dots">
                                    {CAROUSEL_SLIDES.map((_, dotIndex) => (
                                        <button
                                            key={dotIndex}
                                            className={`login-carousel__dot ${
                                                activeSlide === dotIndex
                                                    ? "login-carousel__dot--active"
                                                    : ""
                                            }`}
                                            onClick={() =>
                                                setActiveSlide(dotIndex)
                                            }
                                            aria-label={`Go to slide ${dotIndex + 1}`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right — Login Form */}
            <div className="login-form-section">
                <div className="login-form-wrapper">
                    <h1 className="login-form__title">Welcome back</h1>
                    <p className="login-form__subtitle">
                        Sign in to your Fixdesk account to continue
                    </p>

                    <form onSubmit={handleSubmit} autoComplete="off">
                        <div className="login-form__field">
                            <label className="login-form__label">
                                Username or Email
                            </label>
                            <div className="login-form__input-wrapper">
                                <input
                                    type="text"
                                    name="username"
                                    className="login-form__input"
                                    placeholder="Enter your username or email"
                                    value={formData.username}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="login-form__field">
                            <label className="login-form__label">
                                Password
                            </label>
                            <div className="login-form__input-wrapper">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    className="login-form__input"
                                    placeholder="Enter your password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                    style={{ paddingRight: "48px" }}
                                />
                                <button
                                    type="button"
                                    className="login-form__password-toggle"
                                    onClick={() =>
                                        setShowPassword(!showPassword)
                                    }
                                    aria-label={
                                        showPassword
                                            ? "Hide password"
                                            : "Show password"
                                    }
                                >
                                    {showPassword ? (
                                        <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                            <line
                                                x1="1"
                                                y1="1"
                                                x2="23"
                                                y2="23"
                                            />
                                        </svg>
                                    ) : (
                                        <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="3"
                                            />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="login-form__forgot-row">
                            <button
                                type="button"
                                className="login-form__forgot-link"
                                onClick={() => {}}
                            >
                                Forgot password?
                            </button>
                        </div>

                        <button type="submit" className="login-form__submit" disabled={submitting} aria-busy={submitting}>
                            {submitting ? "Signing in..." : "Sign In"}
                        </button>
                    </form>

                    <div className="login-form__footer">
                        <p className="login-form__footer-text">
                            Powered by{" "}
                            <span className="login-form__footer-brand">
                                Tecnospice
                            </span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
